function readEnv(...keys: string[]) {
  for (const key of keys) {
    const value = process.env[key];
    // Sanea valores: algunos env en Vercel quedaron con '\n' o espacios al final.
    if (value) return value.replace(/\\n/g, '').trim();
  }
  return '';
}

export type DeploymentSurface = 'local' | 'qa' | 'production';
export type AuthMode = 'demo' | 'supabase' | 'unavailable';
export type DataMode = 'local-api' | 'supabase';

export const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
export const isLocalHost = ['localhost', '127.0.0.1'].includes(hostname);
export const isVercelPreviewHost = hostname.endsWith('.vercel.app');
export const isQaHost = hostname === 'qa.conectacrm.tech' || hostname.startsWith('qa.');

export const appEnv = readEnv('REACT_APP_APP_ENV', 'NEXT_PUBLIC_APP_ENV') || 'dev';
export const supabaseUrl = readEnv('REACT_APP_SUPABASE_URL', 'NEXT_PUBLIC_SUPABASE_URL');
export const supabaseKey = readEnv(
  'REACT_APP_SUPABASE_PUBLISHABLE_KEY',
  'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY',
);
export const supabaseSchema = readEnv('REACT_APP_SUPABASE_SCHEMA', 'NEXT_PUBLIC_SUPABASE_SCHEMA');
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey);

// URL base de las Edge Functions. Por defecto se deriva de supabaseUrl.
// Se puede sobreescribir con REACT_APP_FUNCTIONS_URL para casos especiales.
export const functionsBaseUrl =
  readEnv('REACT_APP_FUNCTIONS_URL', 'NEXT_PUBLIC_FUNCTIONS_URL') ||
  (supabaseUrl ? `${supabaseUrl.replace(/\/$/, '')}/functions/v1` : '');

export const deploymentSurface: DeploymentSurface = isLocalHost
  ? 'local'
  : appEnv === 'qa' || isQaHost || isVercelPreviewHost
    ? 'qa'
    : 'production';

const authModeOverride = readEnv('REACT_APP_AUTH_MODE', 'NEXT_PUBLIC_AUTH_MODE');

function resolveAuthMode(): AuthMode {
  // Producción tiene prioridad de seguridad: permanece en demo hasta un corte
  // deliberado, aunque las variables compartidas digan 'supabase'. QA y prod
  // comparten build/variables en Vercel, así que el corte de QA es por hostname.
  if (deploymentSurface === 'production') return 'demo';
  // Override explícito por variable (aplica fuera de producción)
  if (authModeOverride === 'demo') return 'demo';
  if (authModeOverride === 'supabase') return isSupabaseConfigured ? 'supabase' : 'unavailable';
  // QA corre sobre Supabase real (corte por hostname).
  if (deploymentSurface === 'qa') return isSupabaseConfigured ? 'supabase' : 'unavailable';
  // Local en demo para desarrollo rápido.
  return 'demo';
}

export const authMode = resolveAuthMode();

const dataModeOverride = readEnv('REACT_APP_DATA_MODE', 'NEXT_PUBLIC_DATA_MODE');

function resolveDataMode(): DataMode {
  // Producción permanece en local-api hasta un corte deliberado (ver resolveAuthMode).
  if (deploymentSurface === 'production') return 'local-api';
  // Override explícito por variable (aplica fuera de producción)
  if (dataModeOverride === 'supabase') return isSupabaseConfigured ? 'supabase' : 'local-api';
  if (dataModeOverride === 'local-api') return 'local-api';
  // QA sobre Supabase (corte por hostname).
  if (deploymentSurface === 'qa' && isSupabaseConfigured) return 'supabase';
  return 'local-api';
}

export const dataMode: DataMode = resolveDataMode();

export const isLocalSurface = deploymentSurface === 'local';
export const isQaSurface = deploymentSurface === 'qa';
export const isProductionSurface = deploymentSurface === 'production';
export const isDemoAuthMode = authMode === 'demo';
export const isSupabaseAuthMode = authMode === 'supabase';
export const isRuntimeMisconfigured = authMode === 'unavailable';
export const allowsRegistration = isSupabaseAuthMode;
export const allowsDemoReset = isLocalSurface;

export const surfaceLabel =
  deploymentSurface === 'local'
    ? 'Local'
    : deploymentSurface === 'qa'
      ? 'QA'
      : 'Produccion';

export const authLabel =
  authMode === 'demo'
    ? 'Auth demo'
    : authMode === 'supabase'
      ? 'Auth Supabase'
      : 'Auth no disponible';

export const dataLabel = dataMode === 'local-api' ? 'Datos locales' : 'Datos remotos';

